/**
 * A requirement's formatting (28.10.2026): what someone pastes from Claude,
 * Google Docs or Word keeps its lists, headings, bold and links, written as
 * the same small Markdown the guide reads (`guideMarkdown.js`). The text is
 * stored as text and drawn as elements, never as HTML.
 */
import { parseBlocks } from "./guideMarkdown";

const SKIP = new Set(["script", "style", "head", "title", "meta", "link", "template"]);
const BLOCKS = new Set(["p", "div", "section", "article", "header", "footer", "main"]);

function walk(node, ctx) {
  if (node.nodeType === 3) return ctx.pre ? node.nodeValue : node.nodeValue.replace(/\s+/g, " ");
  if (node.nodeType !== 1) return "";
  const tag = node.tagName.toLowerCase();
  if (SKIP.has(tag)) return "";
  const kids = (c = ctx) => Array.from(node.childNodes).map((n) => walk(n, c)).join("");
  // "<b>bold </b>text": the marks hug the words, the spaces stay outside.
  const wrap = (mark) => {
    const all = kids();
    const t = all.trim();
    if (!t) return all;
    return `${/^\s/.test(all) ? " " : ""}${mark}${t}${mark}${/\s$/.test(all) ? " " : ""}`;
  };
  if (tag === "br") return "\n";
  if (tag === "strong" || tag === "b") return wrap("**");
  if (tag === "em" || tag === "i") return wrap("*");
  if (tag === "code") return ctx.pre ? kids() : wrap("`");
  if (tag === "a") {
    const t = kids().trim();
    const href = node.getAttribute("href") || "";
    return t && /^https?:\/\//.test(href) && t !== href ? `[${t}](${href})` : t;
  }
  if (/^h[1-6]$/.test(tag)) {
    const t = kids().replace(/\*\*/g, "").trim();
    return t ? `\n\n${Number(tag[1]) <= 2 ? "##" : "###"} ${t}\n\n` : "";
  }
  if (tag === "ul" || tag === "ol") {
    const pad = "  ".repeat(ctx.depth);
    const items = Array.from(node.children).filter((li) => li.tagName.toLowerCase() === "li");
    const lines = items.map((li, i) => {
      const text = Array.from(li.childNodes).map((n) => walk(n, { ...ctx, li: true, depth: ctx.depth + 1 })).join("");
      return `${pad}${tag === "ol" ? `${i + 1}.` : "-"} ${text.trim()}`;
    });
    return `\n${lines.join("\n")}\n`;
  }
  if (tag === "blockquote") return `\n\n> ${kids().replace(/\s*\n+\s*/g, " ").trim()}\n\n`;
  if (tag === "pre") return `\n\n${kids({ ...ctx, pre: true })}\n\n`;
  if (tag === "hr") return "\n\n";
  if (tag === "table") {
    const rows = Array.from(node.querySelectorAll("tr")).map((tr) =>
      Array.from(tr.children).map((td) => walk(td, ctx).replace(/\s+/g, " ").replace(/\|/g, "/").trim()));
    if (!rows.length) return "";
    const line = (r) => `| ${r.join(" | ")} |`;
    return `\n\n${[line(rows[0]), line(rows[0].map(() => "---")), ...rows.slice(1).map(line)].join("\n")}\n\n`;
  }
  if (BLOCKS.has(tag)) return ctx.li ? `${kids()} ` : `\n\n${kids()}\n\n`;
  return kids();
}

/** Pasted HTML → the requirement's Markdown; "" when there is nothing in it. */
export function htmlToMarkdown(html) {
  if (!html || typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(String(html), "text/html");
  const raw = walk(doc.body, { depth: 0, li: false, pre: false });
  return raw.split("\n")
    .map((l) => (/^\s*([-*]|\d+\.)\s/.test(l) ? l.trimEnd() : l.trim()).replace(/(\S)\s{2,}/g, "$1 "))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** A requirement, read into blocks: the guide's Markdown, plus what people
 *  type or paste as plain text — "# " headings, "•" bullets, line breaks. */
export function textBlocks(text) {
  const normal = String(text || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\t/g, "  ")
    .replace(/^#\s+/gm, "## ")
    .replace(/^#{4,6}\s+/gm, "### ")
    .replace(/^(\s*)[•◦▪‣–]\s+/gm, "$1- ");
  return parseBlocks(normal, { keepLines: true });
}

/** A list block as a tree: each item may carry the sub-list under it. */
export function listTree(block) {
  const root = { ordered: block.ordered, items: [] };
  const stack = [root];
  block.items.forEach((text, k) => {
    const { depth, ordered } = block.levels?.[k] || { depth: 0, ordered: block.ordered };
    while (stack.length - 1 > depth) stack.pop();
    while (stack.length - 1 < depth) {
      const parent = stack[stack.length - 1];
      const last = parent.items[parent.items.length - 1];
      if (!last) break;
      last.sub = last.sub || { ordered, items: [] };
      stack.push(last.sub);
    }
    stack[stack.length - 1].items.push({ text });
  });
  return root;
}
